const APPLIED_KEY = 'virea_geo_applied';
const DISMISSED_KEY = 'virea_geo_dismissed';
const COUNTRY_CACHE_KEY = 'virea_geo_country';
const ATTEMPTS_KEY = 'virea_geo_attempts';
const DISMISS_DAYS = 30;
const MAX_AUTO_ATTEMPTS = 2;

const SELECTORS = {
  root: '[data-geo-auto]',
  data: '[data-geo-banner-data]',
  form: '#geo-auto-form',
  countryInput: '[data-geo-country-input]',
  localeInput: '[data-geo-locale-input]',
  returnTo: '[data-geo-return-to]',
  banner: '[data-geo-banner]',
  bannerFlag: '[data-geo-flag]',
  bannerCountryName: '[data-geo-country-name]',
  bannerCountryNameBtn: '[data-geo-country-name-btn]',
  bannerCountryInput: '[data-geo-banner-country-input]',
  bannerLocaleInput: '[data-geo-banner-locale-input]',
  bannerReturnTo: '[data-geo-banner-return-to]',
  bannerDismiss: '[data-geo-dismiss]',
  bannerClose: '[data-geo-close]',
};

let data;

function isApplied() {
  try {
    return localStorage.getItem(APPLIED_KEY) === '1';
  } catch {
    return false;
  }
}

function setApplied() {
  try {
    localStorage.setItem(APPLIED_KEY, '1');
    sessionStorage.removeItem(ATTEMPTS_KEY);
  } catch {}
}

function isDismissed() {
  try {
    const v = localStorage.getItem(DISMISSED_KEY);
    if (!v) return false;
    const ts = parseInt(v, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_DAYS * 86400000;
  } catch {
    return false;
  }
}

function setDismissed() {
  try {
    localStorage.setItem(DISMISSED_KEY, Date.now().toString());
  } catch {}
}

function getAttempts() {
  try {
    return parseInt(sessionStorage.getItem(ATTEMPTS_KEY) || '0', 10) || 0;
  } catch {
    return 0;
  }
}

function incAttempts() {
  try {
    sessionStorage.setItem(ATTEMPTS_KEY, String(getAttempts() + 1));
  } catch {}
}

async function fetchCountry() {
  try {
    const cached = sessionStorage.getItem(COUNTRY_CACHE_KEY);
    if (cached) return cached;
  } catch {}

  const sources = [
    async () => {
      const res = await fetch('https://api.country.is/', { cache: 'no-store' });
      if (!res.ok) throw new Error(`country.is ${res.status}`);
      const json = await res.json();
      return json.country;
    },
    async () => {
      const res = await fetch('https://ipapi.co/country/', { cache: 'no-store' });
      if (!res.ok) throw new Error(`ipapi ${res.status}`);
      const text = (await res.text()).trim();
      if (!/^[A-Z]{2}$/.test(text)) throw new Error('ipapi bad response');
      return text;
    },
  ];

  for (const source of sources) {
    try {
      const country = await source();
      if (country) {
        try {
          sessionStorage.setItem(COUNTRY_CACHE_KEY, country);
        } catch {}
        return country;
      }
    } catch {}
  }

  return null;
}

function pickLocale(langs) {
  if (!langs || langs.length === 0) return null;
  const navLangs = [
    navigator.language,
    ...(navigator.languages || []),
  ]
    .filter(Boolean)
    .map((l) => l.toLowerCase().split('-')[0]);

  for (const nav of navLangs) {
    const match = langs.find((l) => l.toLowerCase() === nav);
    if (match) return match;
  }

  return langs[0];
}

function currentPath() {
  return window.location.pathname + window.location.search;
}

function autoSubmit(detectedIso, entry) {
  const form = document.querySelector(SELECTORS.form);
  const countryInput = document.querySelector(SELECTORS.countryInput);
  const localeInput = document.querySelector(SELECTORS.localeInput);
  const returnToInput = document.querySelector(SELECTORS.returnTo);

  if (!form || !countryInput) return;

  countryInput.value = detectedIso;

  const lang = pickLocale(entry.langs);
  if (lang && localeInput) localeInput.value = lang;

  if (returnToInput) returnToInput.value = currentPath();

  incAttempts();
  form.submit();
}

function showFallbackBanner(detectedIso, entry) {
  const banner = document.querySelector(SELECTORS.banner);
  if (!banner) return;

  const nameEls = banner.querySelectorAll(SELECTORS.bannerCountryName);
  nameEls.forEach((el) => {
    el.textContent = entry.name;
  });

  const btnNameEl = banner.querySelector(SELECTORS.bannerCountryNameBtn);
  if (btnNameEl) btnNameEl.textContent = entry.name;

  const flagEl = banner.querySelector(SELECTORS.bannerFlag);
  if (flagEl) flagEl.classList.add(`m-country-flags--${detectedIso}`);

  const countryInput = banner.querySelector(SELECTORS.bannerCountryInput);
  const localeInput = banner.querySelector(SELECTORS.bannerLocaleInput);
  const returnToInput = banner.querySelector(SELECTORS.bannerReturnTo);
  if (countryInput) countryInput.value = detectedIso;

  const lang = pickLocale(entry.langs);
  if (lang && localeInput) localeInput.value = lang;
  if (returnToInput) returnToInput.value = currentPath();

  banner.querySelector(SELECTORS.bannerDismiss)?.addEventListener('click', () => {
    setDismissed();
    hideBanner(banner);
  });

  banner.querySelector(SELECTORS.bannerClose)?.addEventListener('click', () => {
    setDismissed();
    hideBanner(banner);
  });

  banner.hidden = false;
  requestAnimationFrame(() => {
    banner.classList.add('is-visible');
  });
}

function hideBanner(banner) {
  banner.classList.remove('is-visible');
  setTimeout(() => {
    banner.hidden = true;
  }, 300);
}

async function init() {
  const root = document.querySelector(SELECTORS.root);
  if (!root) return;

  if (isApplied()) return;
  if (isDismissed()) return;

  const dataEl = root.querySelector(SELECTORS.data);
  if (!dataEl) return;

  try {
    data = JSON.parse(dataEl.textContent);
  } catch {
    return;
  }

  const detected = await fetchCountry();
  if (!detected) return;

  if (detected === data.current) {
    setApplied();
    return;
  }

  const entry = data.countries[detected];
  if (!entry) return;

  if (getAttempts() >= MAX_AUTO_ATTEMPTS) {
    showFallbackBanner(detected, entry);
    return;
  }

  autoSubmit(detected, entry);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
